import { useEffect, type RefObject } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("aria-hidden") && el.offsetParent !== null,
  );
}

interface Options {
  /** Optional: explicitly hand focus to this element on open. Falls
   *  back to the first focusable inside `containerRef`. */
  initialFocusRef?: RefObject<HTMLElement>;
  /** If false, Escape doesn't close. Default true. */
  closeOnEscape?: boolean;
}

/**
 * Modal accessibility primitive: on mount focuses the first focusable
 * (or the explicit initial element), traps Tab/Shift-Tab inside the
 * container, listens for Escape, and restores focus to the
 * previously-active element on unmount.
 *
 * The container element should have `role="dialog"` (and usually
 * `aria-modal="true"`) on the JSX side.
 */
export function useModalFocus(
  containerRef: RefObject<HTMLElement>,
  onClose: () => void,
  opts: Options = {},
): void {
  const { initialFocusRef, closeOnEscape = true } = opts;

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Defer the focus move one tick so Portal/Render order can settle.
    const focusRaf = window.requestAnimationFrame(() => {
      const target =
        initialFocusRef?.current ??
        focusables(root)[0] ??
        root;
      target.focus({ preventScroll: true });
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (!closeOnEscape) return;
        e.stopPropagation();
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables(root);
      if (items.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const active = document.activeElement as HTMLElement | null;
      const idx = active ? items.indexOf(active) : -1;
      if (e.shiftKey) {
        if (idx <= 0) {
          e.preventDefault();
          items[items.length - 1].focus();
        }
      } else {
        if (idx === items.length - 1 || idx === -1) {
          e.preventDefault();
          items[0].focus();
        }
      }
    };

    document.addEventListener("keydown", onKey, true);
    return () => {
      window.cancelAnimationFrame(focusRaf);
      document.removeEventListener("keydown", onKey, true);
      // Return focus to the trigger, but only if it's still mounted —
      // a route change between open and close shouldn't refocus stale
      // DOM (which would silently no-op anyway).
      if (
        previouslyFocused &&
        document.contains(previouslyFocused) &&
        typeof previouslyFocused.focus === "function"
      ) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
